import { useRef, useEffect, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { Building2, MapPin, Banknote, CalendarDays } from "lucide-react";

function AnimatedCounter({ value, suffix = "", prefix = "", duration = 2.2, customFormat = false }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value, duration]);

  // Custom format for transactions (just the number, no M suffix)
  if (customFormat) {
    return (
      <span ref={ref} className="kpi-number">
        {display.toLocaleString("fr-CH")}
      </span>
    );
  }

  // Special formatting for CHF amounts
  const displayFormatted =
    value >= 1000000
      ? `${prefix}${Math.round(display / 1000000)}M${suffix}`
      : `${prefix}${display.toLocaleString("fr-CH")}${suffix}`;

  return (
    <span ref={ref} className="kpi-number">
      {displayFormatted}
    </span>
  );
}

const KPI_ITEMS = [
  {
    key: "nombreProjets",
    label: "Projets",
    icon: Building2,
    suffix: "",
    prefix: "",
  },
  {
    key: "nombreCantons",
    label: "Cantons",
    icon: MapPin,
    suffix: "",
    prefix: "",
  },
  {
    key: "transactionsImmobilieres",
    label: "millions CHF en transactions",
    icon: Banknote,
    suffix: "",
    prefix: "",
    useApiValue: "totalFinancementLeve", // Use dynamic value from API (in millions)
    customFormat: true,
  },
  {
    key: "anneesExperience",
    label: "Années d'expérience",
    icon: CalendarDays,
    suffix: " ans",
    prefix: "",
  },
];

export default function KpiSection({ stats }) {
  return (
    <div
      className="py-24 md:py-32"
      data-testid="kpi-section"
      style={{ background: "rgba(5,5,5,0.8)" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Portrait */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 flex justify-center"
          >
            <div className="relative group">
              {/* Glow behind portrait */}
              <div className="absolute inset-0 rounded-full bg-[#B07D62]/[0.06] blur-3xl scale-110 group-hover:bg-[#B07D62]/[0.1] transition-all duration-700" />

              <motion.div
                whileHover={{ rotateY: 3, rotateX: -2, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 200, damping: 30 }}
                className="relative w-[280px] h-[280px] md:w-[340px] md:h-[340px] rounded-full overflow-hidden border border-white/[0.08] backdrop-blur-md bg-white/[0.02] shadow-[0_20px_60px_rgba(0,0,0,0.4)]"
              >
                <img
                  src="/portrait.png"
                  alt="Julien D'Amore"
                  data-testid="portrait-image"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </motion.div>
            </div>
          </motion.div>

          {/* KPIs */}
          <div className="lg:col-span-7 space-y-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="section-label mb-3">Parcours</p>
              <h2
                className="text-3xl md:text-5xl tracking-tight font-light text-[#FAFAF8] mb-4"
                data-testid="kpi-title"
              >
                Expertise reconnue
              </h2>
              <p className="text-base md:text-lg text-[#FAFAF8]/80 leading-relaxed max-w-lg">
                Depuis 2006, un accompagnement sur-mesure dans le courtage, la
                valorisation et le financement immobilier en Suisse romande.
              </p>
            </motion.div>

            {/* Counter grid */}
            <div className="grid grid-cols-2 gap-6">
              {KPI_ITEMS.map(({ key, label, icon: Icon, suffix, prefix, useApiValue, customFormat }, i) => {
                // Calculate the value to display
                let displayValue = 0;
                if (stats) {
                  if (useApiValue && stats[useApiValue]) {
                    // Convert from CHF to millions (rounded)
                    displayValue = Math.round(stats[useApiValue] / 1000000);
                  } else if (stats[key] !== undefined) {
                    displayValue = stats[key];
                  }
                }
                
                return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    delay: 0.1 * i,
                    duration: 0.6,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  whileHover={{ y: -4, scale: 1.02 }}
                  className="relative glass-panel rounded-2xl p-6 cursor-default transition-all duration-500 hover:bg-white/[0.06] hover:border-white/[0.15] hover:shadow-[0_0_40px_-10px_rgba(74,144,255,0.2)] group overflow-hidden"
                  data-testid={`kpi-card-${key}`}
                >
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#B07D62]/[0.08] via-transparent to-[#B07D62]/[0.04]" />
                  </div>
                  
                  {/* Icon container with glow */}
                  <div className="relative w-10 h-10 rounded-xl bg-[#B07D62]/[0.08] border border-[#B07D62]/[0.12] flex items-center justify-center mb-4 group-hover:bg-[#B07D62]/[0.15] group-hover:border-[#B07D62]/[0.25] group-hover:shadow-[0_0_20px_rgba(74,144,255,0.2)] transition-all duration-500">
                    <Icon
                      size={20}
                      strokeWidth={1.5}
                      className="text-[#B07D62] group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  
                  <p className="relative text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#FAFAF8] mb-2 group-hover:text-white transition-colors duration-500">
                    {stats ? (
                      <AnimatedCounter
                        value={displayValue}
                        suffix={suffix}
                        prefix={prefix}
                        customFormat={customFormat}
                      />
                    ) : (
                      <span className="text-[#415A77]/30">—</span>
                    )}
                  </p>
                  <p className="relative text-xs tracking-[0.1em] uppercase text-[#415A77] group-hover:text-[#B07D62]/70 transition-colors duration-500">
                    {label}
                  </p>
                </motion.div>
              )})}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
